import styled from 'styled-components';
import {
  body2,
  borderRadius,
  caption,
  colors,
  duration,
  easing,
  largeShadow,
  overline,
  slideUpFade,
  spacing,
  zIndex,
} from 'ui/theme';

type StyledOptionProps = {
  hasIcon: boolean;
};

export const StyledActionMenu = styled.div`
  align-self: center;
  display: inline-block;
  position: relative;
`;

export const StyledOptions = styled.div`
  ${largeShadow}
  animation: ${slideUpFade} ${duration.short} ${easing.entrance};
  background: ${colors.backgroundPrimary};
  border-radius: ${borderRadius.lg};
  display: flex;
  flex-direction: column;
  padding: ${spacing.threeEights};
  z-index: ${zIndex.newDropdown};
  min-width: ${spacing.x10};
`;

export const StyledOptionDescription = styled.div`
  ${caption}
  color: ${colors.textSecondary};
  grid-column: 1;
  grid-row: 2;
  margin-top: ${spacing.eighth};
  text-align: left;
  white-space: normal;

  [aria-disabled='true'] & {
    color: ${colors.textDisabled};
  }
`;

export const StyledOption = styled.div<StyledOptionProps>`
  ${body2}
  align-items: center;
  background: none;
  border: none;
  box-sizing: border-box;
  color: ${colors.textPrimary};
  display: grid;
  grid-template-columns: ${({ hasIcon }) => (hasIcon ? `${spacing.plusHalf} auto` : 'auto')};
  grid-column-gap: ${spacing.half};
  justify-items: start;
  padding: ${spacing.half};
  text-decoration: none;
  border-radius: ${borderRadius.md};
  width: 100%;
  white-space: nowrap;
  transition: background-color ${duration.short} ${easing.entrance};

  ${({ hasIcon }) => hasIcon && `${StyledOptionDescription} { grid-column: 2; }`}

  svg {
    justify-self: center;
    align-self: center;
  }

  svg:not(.shape) path {
    stroke: ${colors.iconSubtle};
  }
  svg.shape {
    color: ${colors.iconSubtle};
  }
  svg[data-temporary-fix] path {
    fill: ${colors.iconSubtle};
    stroke: none;
  }

  &:focus {
    background: ${colors.backgroundInteractive};
    color: ${colors.textInteractiveHover};
    text-decoration: none;
    outline: none;

    ${StyledOptionDescription} {
      color: ${colors.textLink};
    }

    svg:not(.shape) path {
      stroke: ${colors.iconInteractiveHover};
    }
    svg.shape {
      color: ${colors.iconInteractiveHover};
    }
    svg[data-temporary-fix] path {
      fill: ${colors.iconInteractiveHover};
      stroke: none;
    }
  }

  &:hover {
    background: ${colors.backgroundPrimaryHover};
    color: inherit;
    text-decoration: none;

    ${StyledOptionDescription} {
      color: ${colors.textSecondary};
    }

    svg:not(.shape) path {
      stroke: ${colors.iconPrimaryHover};
    }
    svg.shape {
      color: ${colors.iconPrimaryHover};
    }
    svg[data-temporary-fix] path {
      fill: ${colors.iconPrimaryHover};
      stroke: none;
    }
  }

  &[data-destructive='true'] {
    color: ${colors.textDestructive};

    svg:not(.shape) path {
      stroke: ${colors.textDestructive};
    }
    svg.shape {
      color: ${colors.textDestructive};
    }
    svg[data-temporary-fix] path {
      fill: ${colors.textDestructive};
      stroke: none;
    }

    &:focus,
    &:hover {
      background: ${colors.salmon10};
      text-decoration: none;
      outline: none;
    }
  }

  &[aria-disabled='true'] {
    color: ${colors.textDisabled};
    cursor: not-allowed;

    svg:not(.shape) path {
      stroke: ${colors.iconDisabled};
    }
    svg.shape {
      color: ${colors.iconDisabled};
    }
    svg[data-temporary-fix] path {
      fill: ${colors.iconDisabled};
      stroke: none;
    }

    &:focus {
      background: ${colors.backgroundPrimary};
      border-radius: ${spacing.quarter};
      color: ${colors.textDisabled};
      text-decoration: none;
      outline: none;

      ${StyledOptionDescription} {
        color: ${colors.textDisabled};
      }
    }

    &:hover {
      background: ${colors.backgroundPrimary};
      border-radius: ${borderRadius.md};
      color: ${colors.textDisabled};
      cursor: not-allowed;
      text-decoration: none;

      ${StyledOptionDescription} {
        color: ${colors.textDisabled};
      }
    }
  }
`;

/**
 * border: none; removes the inset/beveled browser defaults
 * border-top applies it as a basic line
 */

export const Divider = styled.hr`
  margin: ${spacing.threeEights} -${spacing.threeEights};
  color: ${colors.borderPrimarySubtle};
  border: none;
  border-top: 1px solid ${colors.borderPrimarySubtle};
`;

export const TitleOption = styled.div`
  ${overline};
  color: ${colors.textSecondary};
  padding: ${spacing.threeEights} ${spacing.half};
`;
